import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Edit2, Trash2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Pager from "@/components/site/Pager";
import { api } from "@/lib/api";
import { toast } from "sonner";
import useDragReorder from "@/lib/useDragReorder";

const PAGE_SIZE = 20;

export default function AdminBlog() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const load = () => api.get("/admin/blog", { params: { page, page_size: PAGE_SIZE } })
    .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);

  const remove = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await api.delete(`/admin/blog/${id}`);
    toast.success("Deleted"); load();
  };

  const canReorder = page === 1;
  const drag = useDragReorder({
    items,
    getId: (p) => p.id,
    onReorder: async (newList, ids) => {
      setItems(newList);
      try { await api.post("/admin/blog/reorder", { ids }); toast.success("Order updated"); }
      catch { toast.error("Failed to save order"); load(); }
    },
  });

  return (
    <div className="space-y-6" data-testid="admin-blog">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-2xl font-semibold text-slate-900">Career Blog</h1><p className="text-sm text-slate-500">{total} article{total === 1 ? "" : "s"}{canReorder ? " · drag to reorder" : ""}</p></div>
        <Link to="/admin/blog/new"><Button className="bg-primary hover:bg-primary/90" data-testid="blog-new"><Plus className="mr-1 h-4 w-4" />New article</Button></Link>
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr>
              {canReorder && <th className="w-10 px-2 py-3"></th>}
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Author</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Views</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => {
              const rowExtras = canReorder ? drag.rowProps(p) : {};
              const rowCls = canReorder ? drag.rowClass(p) : "";
              return (
                <tr key={p.id} {...rowExtras} className={`border-b last:border-0 transition-colors ${rowCls || "hover:bg-slate-50"}`} data-testid={`blog-row-${p.id}`}>
                  {canReorder && (
                    <td className="w-10 px-2 py-3 text-center"><drag.Handle testId={`blog-row-${p.id}-handle`} /></td>
                  )}
                  <td className="px-4 py-3"><div className="font-medium text-slate-900">{p.title}</div><div className="text-xs text-slate-500">{new Date(p.published_at).toLocaleDateString()}</div></td>
                  <td className="px-4 py-3 text-slate-700">{p.author}</td>
                  <td className="px-4 py-3"><Badge className={p.is_published ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-700"}>{p.is_published ? "Published" : "Draft"}</Badge></td>
                  <td className="px-4 py-3 text-slate-700">{p.view_count || 0}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Link to={`/blog/${p.slug}`} target="_blank"><Button variant="ghost" size="sm"><ExternalLink className="h-3.5 w-3.5" /></Button></Link>
                      <Link to={`/admin/blog/${p.id}`}><Button variant="ghost" size="sm" data-testid={`blog-edit-${p.id}`}><Edit2 className="h-3.5 w-3.5" /></Button></Link>
                      <Button variant="ghost" size="sm" onClick={() => remove(p.id)} data-testid={`blog-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={canReorder ? 6 : 5} className="px-4 py-10 text-center text-slate-400">No articles yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} testIdPrefix="admin-blog-pager" />
    </div>
  );
}
